import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Check, X, Download, RefreshCw, DollarSign, MousePointerClick, Coins, Percent, FileSpreadsheet,
} from "lucide-react";

const mediaUrl = (url, download) => {
  if (!url) return "";
  const abs = url.startsWith("/api/") ? `${API}${url.slice(4)}` : url;
  return download ? `${abs}${abs.includes("?") ? "&" : "?"}download=1` : abs;
};

const isVideo = (f) =>
  (f.content_type || "").startsWith("video/") || /\.(mp4|webm|mov)(\?|$)/i.test(f.url || f || "");

const STATUSES = [
  ["pending_review", "Pending review"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
  ["started", "Started"],
  ["", "All"],
];

const Proof = ({ file, testid }) => (
  <div className="relative h-40 overflow-hidden border border-border bg-secondary/40">
    {isVideo(file) ? (
      <video src={mediaUrl(file.url)} controls className="h-full w-full object-contain" data-testid={testid}>
        <track kind="captions" />
      </video>
    ) : (
      <img src={mediaUrl(file.url)} alt="proof" className="h-full w-full object-contain" data-testid={testid} />
    )}
    <a href={mediaUrl(file.url, true)}
      className="absolute bottom-1 right-1 border border-border bg-background/85 p-1 text-primary"
      data-testid={`${testid}-download`}>
      <Download className="h-3.5 w-3.5" />
    </a>
  </div>
);

function StepQueue({ note, onReviewed }) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/quest-steps", { params: { status: "pending_review" } });
    setRows(data);
    setSelected([]);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (attemptId, stepKey, status) => {
    try {
      const { data } = await api.post(`/admin/quest-attempts/${attemptId}/review-step`, {
        step_key: stepKey, status, note: note.current || "",
      });
      toast.success(status === "approved" ? `Approved — ${fmt(data.points)} points awarded` : "Rejected");
      load();
      onReviewed();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const bulk = async (status) => {
    if (selected.length === 0) {
      toast.error("Select at least one submission");
      return;
    }
    try {
      const items = selected.map((id) => {
        const [attempt_id, step_key] = id.split("::");
        return { attempt_id, step_key };
      });
      const { data } = await api.post("/admin/quest-steps/bulk-review", {
        items, status, note: note.current || "",
      });
      toast.success(`${data.reviewed} submission(s) ${status}`);
      load();
      onReviewed();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // all-at-once quests are reviewed as one bundle per attempt
  const groups = [];
  const byAttempt = {};
  rows.forEach((r) => {
    if (r.step_mode === "single") {
      if (!byAttempt[r.attempt_id]) {
        byAttempt[r.attempt_id] = { ...r, bundle: [r] };
        groups.push(byAttempt[r.attempt_id]);
      } else {
        byAttempt[r.attempt_id].bundle.push(r);
      }
    } else {
      groups.push({ ...r, bundle: [r] });
    }
  });

  if (rows.length === 0) return null;

  return (
    <div className="mt-8" data-testid="step-queue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold md:text-lg">
          Step submissions <span className="text-muted-foreground">({rows.length})</span>
        </h3>
        <div className="flex gap-2">
          <Button size="sm" className="rounded-none" onClick={() => bulk("approved")}
            data-testid="step-bulk-approve">
            <Check className="mr-2 h-3.5 w-3.5" /> Approve selected ({selected.length})
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => bulk("rejected")}
            data-testid="step-bulk-reject">
            <X className="mr-2 h-3.5 w-3.5" /> Reject selected
          </Button>
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        {groups.map((g) => {
          const key = `${g.attempt_id}::${g.step_mode === "single" ? "__all__" : g.step_key}`;
          return (
            <div key={key} className="border border-border bg-card" data-testid={`step-card-${g.attempt_id}-${g.step_key}`}>
              <div className="flex items-start justify-between gap-3 border-b border-border p-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {g.brand_name} · {g.quest_title}
                  </div>
                  <h4 className="mt-1 font-semibold">
                    {g.step_mode === "single"
                      ? `All ${g.bundle.length} step(s)`
                      : `Step ${g.step_index}/${g.step_count} — ${g.label}`}
                  </h4>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {g.username} · {(g.submitted_at || "").slice(0, 16).replace("T", " ")}
                    {g.period_key !== "once" ? ` · ${g.period_key}` : ""}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="mono text-sm text-primary">
                    +{fmt(g.bundle.reduce((n, b) => n + b.points, 0))} pts
                  </span>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input type="checkbox" checked={selected.includes(key)}
                      onChange={() => setSelected((s) =>
                        s.includes(key) ? s.filter((x) => x !== key) : [...s, key])}
                      data-testid={`step-select-${g.attempt_id}-${g.step_key}`} />
                    select
                  </label>
                </div>
              </div>

              <div className="space-y-5 p-5">
                {g.bundle.map((b) => (
                  <div key={b.step_key}>
                    {g.step_mode === "single" && (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-primary">
                        Step {b.step_index} — {b.label} (+{fmt(b.points)})
                      </div>
                    )}
                    {b.instruction && (
                      <p className="mt-1 text-xs text-muted-foreground">Asked for: {b.instruction}</p>
                    )}
                    {Object.keys(b.fields || {}).length > 0 && (
                      <div className="mt-2 border border-border/60">
                        {Object.entries(b.fields).map(([k, v]) => (
                          <div key={k} className="flex justify-between gap-4 border-b border-border/40 px-3 py-2 text-xs last:border-0">
                            <span className="text-muted-foreground">{b.field_labels?.[k] || k}</span>
                            <span className="mono">{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {b.note && <p className="mt-2 text-sm text-muted-foreground">“{b.note}”</p>}
                    {(b.files || []).length > 0 && (
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        {b.files.map((f, i) => (
                          <Proof key={f.url} file={f} testid={`step-proof-${b.attempt_id}-${b.step_key}-${i}`} />
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex gap-2">
                  <Button size="sm" className="rounded-none"
                    onClick={() => review(g.attempt_id, g.step_mode === "single" ? "__all__" : g.step_key, "approved")}
                    data-testid={`step-approve-${g.attempt_id}-${g.step_key}`}>
                    <Check className="mr-2 h-3.5 w-3.5" /> Approve &amp; award
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none"
                    onClick={() => review(g.attempt_id, g.step_mode === "single" ? "__all__" : g.step_key, "rejected")}
                    data-testid={`step-reject-${g.attempt_id}-${g.step_key}`}>
                    <X className="mr-2 h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export const QuestReview = () => {
  const [status, setStatus] = useState("pending_review");
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState([]);
  const noteRef = { current: note };

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/quest-attempts", { params: { status } });
    setRows(data);
    setSelected([]);
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, next) => {
    try {
      const { data } = await api.post(`/admin/quest-attempts/${id}/review`, { status: next, note });
      toast.success(next === "approved" ? `Approved — ${fmt(data.points || 0)} points awarded` : "Rejected");
      setNote("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const bulk = async (next) => {
    if (selected.length === 0) {
      toast.error("Select at least one submission");
      return;
    }
    try {
      const { data } = await api.post("/admin/quest-attempts/bulk-review", {
        ids: selected, status: next, note,
      });
      toast.success(`${data.reviewed} submission(s) ${next}`);
      setNote("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const singleProofRows = rows.filter((r) => !r.steps || Object.keys(r.steps).length === 0);

  return (
    <div data-testid="quest-review">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(([v, l]) => (
            <button
              key={v || "all"}
              type="button"
              onClick={() => setStatus(v)}
              data-testid={`review-filter-${v || "all"}`}
              className={`border px-3 py-1.5 text-[11px] uppercase tracking-[0.16em] ${
                status === v ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <Button variant="outline" size="sm" className="rounded-none" onClick={load} data-testid="review-refresh">
          <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3 border border-border bg-card p-4">
        <Input
          className="max-w-md rounded-none bg-secondary"
          placeholder="Note / reject reason (applies to the next action)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="review-note"
        />
        <span className="text-xs text-muted-foreground">
          Approving pays the player instantly and books the campaign CPA as revenue.
        </span>
      </div>

      {status === "pending_review" && (
        <StepQueue note={noteRef} onReviewed={load} />
      )}

      {singleProofRows.length === 0 && (
        <p className="mt-6 border border-border bg-card p-8 text-center text-sm text-muted-foreground"
          data-testid="review-empty">
          No single-proof submissions in this view.
        </p>
      )}

      {singleProofRows.length > 0 && status === "pending_review" && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <h3 className="text-base font-semibold md:text-lg">
            Single-proof submissions <span className="text-muted-foreground">({singleProofRows.length})</span>
          </h3>
          <Button size="sm" className="rounded-none" onClick={() => bulk("approved")}
            data-testid="review-bulk-approve">
            <Check className="mr-2 h-3.5 w-3.5" /> Approve selected ({selected.length})
          </Button>
          <Button size="sm" variant="outline" className="rounded-none" onClick={() => bulk("rejected")}
            data-testid="review-bulk-reject">
            <X className="mr-2 h-3.5 w-3.5" /> Reject selected
          </Button>
        </div>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        {singleProofRows.map((r) => (
          <div key={r.id} className="border border-border bg-card" data-testid={`review-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3 border-b border-border p-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {r.brand_name} · {r.campaign_name}
                </div>
                <h4 className="mt-1 font-semibold">{r.quest_title}</h4>
                <div className="mt-1 text-xs text-muted-foreground">
                  {r.username} · {(r.submitted_at || r.created_at || "").slice(0, 16).replace("T", " ")}
                  {r.period_key !== "once" ? ` · ${r.period_key}` : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge variant="outline" className="rounded-none uppercase tracking-wider">{r.status}</Badge>
                <span className="mono text-sm text-primary">+{fmt(r.quest_points)} pts</span>
                {r.status === "pending_review" && (
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={selected.includes(r.id)}
                      onChange={() => setSelected((s) =>
                        s.includes(r.id) ? s.filter((x) => x !== r.id) : [...s, r.id])}
                      data-testid={`review-select-${r.id}`}
                    />
                    select
                  </label>
                )}
              </div>
            </div>

            {r.proof_url && (
              <div className="border-b border-border">
                <div className="flex h-56 items-center justify-center bg-secondary/40">
                  {isVideo({ url: r.proof_url }) ? (
                    <video src={mediaUrl(r.proof_url)} controls className="h-full w-full object-contain"
                      data-testid={`review-video-${r.id}`}>
                      <track kind="captions" />
                    </video>
                  ) : (
                    <img src={mediaUrl(r.proof_url)} alt="proof" className="h-full w-full object-contain"
                      data-testid={`review-img-${r.id}`} />
                  )}
                </div>
                <a
                  href={mediaUrl(r.proof_url, true)}
                  className="flex items-center gap-2 px-5 py-3 text-xs text-primary hover:underline"
                  data-testid={`review-download-${r.id}`}
                >
                  <Download className="h-3.5 w-3.5" /> Download original
                </a>
              </div>
            )}

            <div className="space-y-3 p-5">
              {r.proof_note && <p className="text-sm text-muted-foreground">“{r.proof_note}”</p>}
              {r.proof_instructions && (
                <p className="text-xs text-muted-foreground">Asked for: {r.proof_instructions}</p>
              )}
              {r.conversion_source && (
                <p className="text-xs text-muted-foreground">
                  Confirmed via {r.conversion_source} · ${r.cpa_amount} CPA
                </p>
              )}
              {r.reject_reason && <p className="text-xs text-red-400">Rejected: {r.reject_reason}</p>}
              {r.status !== "approved" && (
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-none" onClick={() => review(r.id, "approved")}
                    data-testid={`review-approve-${r.id}`}>
                    <Check className="mr-2 h-3.5 w-3.5" /> Approve &amp; award
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none"
                    onClick={() => review(r.id, "rejected")} data-testid={`review-reject-${r.id}`}>
                    <X className="mr-2 h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{label}</span>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="mono mt-4 text-3xl font-semibold">{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export const QuestRevenue = () => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get("/admin/quests/analytics");
    setData(res.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/quests/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "liquidrp-quest-conversions.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const t = data.totals;

  return (
    <div data-testid="quest-revenue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-base font-semibold md:text-lg">Brand performance</h3>
        <Button variant="outline" size="sm" className="rounded-none" onClick={exportCsv}
          data-testid="quest-export-csv">
          <FileSpreadsheet className="mr-2 h-3.5 w-3.5" /> Export conversions (CSV)
        </Button>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-4">
        <Kpi icon={DollarSign} label="Brand revenue" value={`$${fmt(Math.round(t.revenue))}`}
          sub={`${fmt(t.approved)} approved conversions`} testid="kpi-quest-revenue" />
        <Kpi icon={MousePointerClick} label="Quest starts" value={fmt(t.clicks)}
          sub={`${fmt(t.pending)} awaiting review`} testid="kpi-quest-clicks" />
        <Kpi icon={Percent} label="Conversion rate" value={`${t.conversion_rate}%`}
          sub={`${fmt(t.rejected)} rejected`} testid="kpi-quest-cvr" />
        <Kpi icon={Coins} label="Points paid out" value={fmt(t.points_paid)}
          sub="To players for quests" testid="kpi-quest-points" />
      </div>

      <div className="mt-8 border border-border bg-card p-6">
        <h4 className="text-base font-semibold md:text-lg">Conversions — last 14 days</h4>
        <div className="mt-6 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0b1120", border: "1px solid #1f2937" }} />
              <Bar dataKey="conversions" fill="#22d3ee" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {[
          ["By brand", data.by_brand, "brand"],
          ["By quest", data.by_quest, "quest"],
        ].map(([title, brows, key]) => (
          <div key={title} className="border border-border bg-card">
            <h4 className="border-b border-border px-5 py-4 text-base font-semibold md:text-lg">{title}</h4>
            {brows.length === 0 && <p className="p-5 text-sm text-muted-foreground">No data yet.</p>}
            {brows.map((r) => (
              <div key={r[key]} className="flex items-center justify-between border-b border-border/60 px-5 py-3 last:border-0">
                <div>
                  <div className="text-sm">{r[key]}</div>
                  <div className="text-xs text-muted-foreground">
                    {fmt(r.clicks)} starts · {fmt(r.conversions)} conversions · {fmt(r.points_paid)} pts
                  </div>
                </div>
                <span className="mono text-sm text-primary">${fmt(Math.round(r.revenue))}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-8 border border-border bg-card">
        <h4 className="border-b border-border px-5 py-4 text-base font-semibold md:text-lg">
          Tracking callbacks (latest 40)
        </h4>
        {data.recent_postbacks.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground">
            No postbacks or pixel hits yet. Share the tracking URLs with the brand from Brands &amp; campaigns.
          </p>
        )}
        {data.recent_postbacks.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 px-5 py-3 last:border-0"
            data-testid={`postback-row-${p.id}`}>
            <div className="mono text-xs">
              {p.test && <span className="mr-2 border border-border px-1 text-[10px] text-muted-foreground">TEST</span>}
              <span className={p.result === "approved" ? "text-emerald-400" : "text-amber-400"}>
                {p.result}
              </span>{" "}
              · {p.source} · {p.event || "conversion"} · click {String(p.click_id || "").slice(0, 12)}…
            </div>
            <div className="text-xs text-muted-foreground">
              {p.ip} · {(p.created_at || "").slice(0, 19).replace("T", " ")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
